/**
 * Site comparison page — grouped bar chart + expandable site detail cards.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useDashboardContext } from '@/components/layout/DashboardShell';
import { SiteBarChart } from '@/components/dashboard/SiteBarChart';
import { computeSiteBarData, computeSiteRankings } from '@/lib/dashboard/aggregations';
import { SUBSCALE_META } from '@/lib/dashboard/types';

function SiteDetailCard({ ranking }: { ranking: ReturnType<typeof computeSiteRankings>[0] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-muted/50"
      >
        <div>
          <p className="font-medium">{ranking.siteName}</p>
          <p className="text-xs text-muted-foreground">
            {ranking.observationCount} observation{ranking.observationCount !== 1 ? 's' : ''} · Average:{' '}
            <span className="font-semibold">
              {ranking.average !== null ? ranking.average.toFixed(2) : '—'}
            </span>
          </p>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t px-5 py-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {SUBSCALE_META.map((s) => {
              const val = ranking.subscaleAverages[s.id];
              return (
                <div key={s.id} className="rounded-lg border p-3 text-center">
                  <div className="h-2 w-full rounded-full" style={{ backgroundColor: s.color + '30' }}>
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${((val ?? 0) / 7) * 100}%`, backgroundColor: s.color }}
                    />
                  </div>
                  <p className="mt-2 text-lg font-bold" style={{ color: s.color }}>
                    {val !== null ? val.toFixed(2) : '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">{s.shortName}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function SiteComparisonPage() {
  const { filtered } = useDashboardContext();
  const barData = computeSiteBarData(filtered);
  const rankings = computeSiteRankings(filtered);

  if (filtered.length === 0) {
    return (
      <div className="mx-auto flex max-w-7xl flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <p className="text-lg font-medium">No observations to compare</p>
        <p className="text-sm text-muted-foreground">Adjust your filters or submit more observations.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-6 py-6">
      <SiteBarChart data={barData} />
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Site Details
        </h3>
        {rankings.map((r) => (
          <SiteDetailCard key={r.siteName} ranking={r} />
        ))}
      </div>
    </div>
  );
}
